// CAMINHO: src/components/EditProfileModal.tsx (VERSÃO COM TODOS OS CAMPOS)

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Loader2, UploadCloud } from 'lucide-react';

// A função de corte de imagem não muda...
function getCroppedImg(image: HTMLImageElement, crop: Crop): Promise<Blob> {
    // ... (código da função de corte omitido para brevidade, mantenha o que já tem)
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return Promise.reject();
    ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
    return new Promise((resolve, reject) => { canvas.toBlob(blob => blob ? resolve(blob) : reject(), 'image/jpeg'); });
}


interface EditProfileModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditProfileModal = ({ isOpen, onOpenChange }: EditProfileModalProps) => {
  const { user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  
  // ✅ ADICIONAMOS OS NOVOS CAMPOS AO ESTADO DO FORMULÁRIO
  const [formData, setFormData] = useState({
    fullName: '',
    oabNumber: '',
    phone: '',
    areaAtuacao: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Estados para o corte da imagem
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      // ✅ CARREGAMOS OS DADOS DOS NOVOS CAMPOS
      setFormData({
        fullName: profile.full_name || '',
        oabNumber: profile.oab_number || '',
        phone: profile.phone || '',
        areaAtuacao: (profile.specialization || []).join(', '),
      });
    }
  }, [profile, isOpen]);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, width, height), width, height);
    setCrop(initialCrop);
  };

  const handleSaveChanges = async () => {
    if (!user || !profile) return;
    setIsSaving(true);

    try {
      let avatarUrl = profile.avatar_url;

      if (completedCrop && imgRef.current) {
        const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop);
        const fileName = `${user.id}/${Date.now()}.jpg`;
        const { error: uploadError } = await supabase.storage.from('avatars').upload(fileName, croppedImageBlob, { cacheControl: '3600', upsert: true });
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
        avatarUrl = publicUrl;
      }

      // ✅ PREPARAMOS OS DADOS DOS NOVOS CAMPOS PARA ATUALIZAR
      const updates = {
        full_name: formData.fullName,
        oab_number: formData.oabNumber,
        phone: formData.phone,
        // Várias áreas separadas por vírgula
        specialization: formData.areaAtuacao.split(',').map(area => area.trim()).filter(Boolean),
        avatar_url: avatarUrl,
        updated_at: new Date().toISOString(),
      };

      const { error: profileError } = await supabase.from('profiles').update(updates).eq('user_id', user.id);
      if (profileError) throw profileError;

      toast({ title: "Sucesso!", description: "Seu perfil foi atualizado." });
      await refreshProfile();
      onOpenChange(false);
      setImgSrc('');

    } catch (error: any) {
      toast({ title: "Erro", description: error.message || "Não foi possível salvar o perfil.", variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };
  
  const label = 'block text-sm font-medium text-slate-700';
  const input = 'w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30';
  const LOGO = '/imgs/logos allana/letra_A_logo.png';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
        <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]" />
          <div className="relative flex items-center gap-4 text-left">
            <img src={LOGO} alt="Allana Sthéfany" className="h-12 w-12 shrink-0 select-none object-contain opacity-80" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Meu perfil</p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">Editar perfil</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-4">
          <div className="flex items-center gap-4 rounded-xl bg-[#faf7f2] p-4">
            <div className="rounded-full bg-gradient-to-br from-[#e8d5a8] to-[#9a7f52] p-[2px]">
              <Avatar className="h-16 w-16 border-2 border-white">
                <AvatarImage src={profile?.avatar_url || ''} className="object-cover" />
                <AvatarFallback className="bg-white text-lg font-semibold text-[#9a7f52]">{profile?.full_name?.charAt(0)}</AvatarFallback>
              </Avatar>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800">Foto do perfil</p>
              <p className="text-xs text-slate-500">Escolha uma imagem e ajuste o recorte.</p>
            </div>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="rounded-xl border-slate-200 bg-white text-[13px] shadow-none">
              <UploadCloud className="mr-2 h-4 w-4 text-[#9a7f52]" />
              Trocar foto
            </Button>
            <Input type="file" accept="image/*" onChange={onSelectFile} ref={fileInputRef} className="hidden" />
          </div>

          {imgSrc && (
            <div className="flex justify-center rounded-xl border border-slate-200 p-2">
              <ReactCrop crop={crop} onChange={(_, p) => setCrop(p)} onComplete={(c) => setCompletedCrop(c)} aspect={1} circularCrop>
                <img ref={imgRef} src={imgSrc} onLoad={onImageLoad} style={{ maxHeight: '360px' }} />
              </ReactCrop>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="fullName" className={label}>Nome completo</Label>
              <Input id="fullName" value={formData.fullName} onChange={(e) => setFormData(p => ({ ...p, fullName: e.target.value }))} className={input} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="oabNumber" className={label}>Número da OAB</Label>
              <Input id="oabNumber" value={formData.oabNumber} onChange={(e) => setFormData(p => ({ ...p, oabNumber: e.target.value }))} placeholder="PE 00000" className={input} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className={label}>Telefone</Label>
              <Input id="phone" value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="(81) 99999-9999" className={input} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="areaAtuacao" className={label}>Áreas de atuação</Label>
              <Input id="areaAtuacao" value={formData.areaAtuacao} onChange={(e) => setFormData(p => ({ ...p, areaAtuacao: e.target.value }))} placeholder="Direito Civil, Previdenciário, Consumidor" className={input} />
              <p className="text-xs text-slate-400">Separe as áreas por vírgula.</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
          <DialogClose asChild><Button type="button" variant="outline" className="rounded-xl">Cancelar</Button></DialogClose>
          <Button type="button" onClick={handleSaveChanges} disabled={isSaving} className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
